import { EconomicEventOutput } from '@api';
import { Alert, Button } from '@design';
import { useRunScenario } from '@terminal/hooks/use-run-scenario/useRunScenario';
import { appPaths } from '@terminal/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ConditionOptions } from '../utils/getConditionOptions';
import { AssetsDropdown } from './AssetsDropdown';
import { ConditionsDropdown } from './ConditionsDropdown';
import styles from './RunScenario.module.scss';

export enum AssetsConstituents {
  SPX = 'spx',
  FTSE100 = 'ftse100',
  DJI = 'dji',
  NI225 = 'ni225',
}

export const indexAbbreviationToFullNameMap = {
  [AssetsConstituents.SPX]: 'S&P 500',
  [AssetsConstituents.FTSE100]: 'FTSE 100',
  [AssetsConstituents.DJI]: 'Dow Jones Industrial',
  [AssetsConstituents.NI225]: 'Nikkei 225',
};

export interface WatchlistsOptions {
  key: string;
  name: string;
  isChecked: boolean;
}

export interface RunScenarioProps {
  data: EconomicEventOutput;
  conditionOptions: ConditionOptions[];
}

export interface SelectedAssetItem {
  assetFilterValueKey: string;
  id: string;
}

export const RunScenario = ({ data, conditionOptions }: RunScenarioProps) => {
  const { t } = useTranslation(['calendar']);
  const navigate = useNavigate();
  const { trackScenarioOpen } = usePosthogEvents();

  const [selectedAssets, setSelectedAssets] = useState<SelectedAssetItem>();
  const [showError, setShowError] = useState(false);
  const { lists } = useWatchlist();
  const [customConditionOptions, setCustomConditionOptions] =
    useState<ConditionOptions>();
  const { initializeAssetFilterOptions, initializeConditionFilterOptions } =
    useRunScenario({
      data,
      selectedAssets,
      customConditionOptions,
    });

  const handleSelectAssets = (args: SelectedAssetItem) => {
    if (showError && !!customConditionOptions) {
      setShowError(false);
    }

    setSelectedAssets(args);
  };

  const handleSetCustomCondition = (config: ConditionOptions) => {
    if (showError && !!selectedAssets) {
      setShowError(false);
    }

    setCustomConditionOptions(config);
  };

  return (
    <div className={styles.scenarioRoot}>
      <h2 className={styles.scenarioTitle}>{t('calendar:scenario.title')}</h2>
      <div className={styles.filterGroupWrapper}>
        {showError && (
          <Alert
            className={styles.alert}
            data-testid="alert-error"
            variant="inline"
            message={t('calendar:addAssetsAndConditions')}
          />
        )}
        <AssetsDropdown
          selectedAssets={selectedAssets}
          watchlistLists={lists}
          handleSelectAssets={handleSelectAssets}
        />
        <ConditionsDropdown
          name={data.name}
          customCondition={customConditionOptions}
          handleSetCustomCondition={handleSetCustomCondition}
          conditionOptions={conditionOptions}
        />
        <Button
          className={styles.styledButton}
          label={t('calendar:scenario.runScenario')}
          size="small"
          fullWidth={false}
          onClick={() => {
            if (!selectedAssets || !customConditionOptions) {
              setShowError(true);
              return;
            }

            initializeAssetFilterOptions();
            initializeConditionFilterOptions();
            trackScenarioOpen(POSTHOG_TRACKING_SOURCES.ECONOMIC_EVENT_DRAWER);
            navigate(appPaths.scenario);
          }}
          data-testid="btn-navigate-to-scenario"
        />
      </div>
    </div>
  );
};
